import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DataTable from './common/DataTable';
import Alert from './common/Alert';
import ChatModal from './common/ChatModal';
import AsistenteService from '../services/AsistenteService';
import RegionalService from '../services/RegionalService';
import LoadingSpinner from './common/LoadingSpinner';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import ProcesosMenu from './common/ProcesosMenu';
import './MyAssistants.css';

const isProfessorUser = (currentUser) => {
  if (!currentUser) return false;
  const roleName = (currentUser.roleName || currentUser.nombreRol || currentUser.role || '').toLowerCase();
  return roleName === 'profesor' || roleName.includes('profesor') || roleName.includes('profe');
};

const MyAssistants = () => {
  const { user, logout, isLoading: authLoading } = useAuth();
  const isProfesor = isProfessorUser(user);
  const roleName = (user?.roleName || user?.nombreRol || user?.role || user?.rol || '').toLowerCase();
  const isAdminUser = Boolean(user) && (roleName.includes('admin'));
  const isAllowedUser = isProfesor || isAdminUser;
  const navigate = useNavigate();
  const featureFlags = RegionalService.getFeatureFlags();
  const isGptClassicEnabled = featureFlags.some((flag) => {
    const nombre = (flag?.nombre || flag?.Nombre || '').toLowerCase();
    return nombre === 'asistentesclasicos gpt' && Boolean(flag?.activo ?? flag?.Activo);
  });
  
  // Estados
  const [asistentes, setAsistentes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const isLoadingRef = useRef(false);
  const [assistantToDelete, setAssistantToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Cargar asistentes al montar el componente
  useEffect(() => {
    if (authLoading) return;

    if (!isAllowedUser) {
      setIsLoading(false);
      return;
    }

    if (!hasLoaded && !isLoadingRef.current) {
      loadAsistentes();
    }
  }, [hasLoaded, isAllowedUser, authLoading]);

  const loadAsistentes = async () => {
    if (authLoading) return;
    if (!isAllowedUser) {
      setIsLoading(false);
      return;
    }
    // Evitar llamadas duplicadas
    if (isLoadingRef.current) {
      return;
    }

    try {
      isLoadingRef.current = true;
      setIsLoading(true);
      setAlert({ show: false, message: '', type: 'error', title: '' });
      
      const response = await AsistenteService.getMisAsistentes();
      
      if (response.ok && response.asistentes) {
        const filtered = response.asistentes.filter((item) => {
          const flag = item?.gptClasicoCreacionFinalizada ?? item?.GPTClasicoCreacionFinalizada;
          return flag !== true;
        });
        setAsistentes(filtered);
      } else {
        throw new Error(response.error || 'Error al cargar los asistentes');
      }
      
    } catch (error) {
      console.error('Error al cargar asistentes:', error);
      setAlert({
        show: true,
        message: error.message || 'Error al cargar los asistentes. Inténtalo nuevamente.',
        type: 'error'
      });
      setAsistentes([]);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
      isLoadingRef.current = false;
    }
  };

  const handleRefresh = () => {
    if (!isAllowedUser) return;
    setHasLoaded(false); // Permitir nueva carga
    isLoadingRef.current = false; // Resetear el flag de carga
    loadAsistentes();
  };

  const handleNewAssistant = () => {
    navigate('/new-assistant');
  };

  const handleLogout = () => {
    logout();
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  const handleTestAssistant = (assistant) => {
    setSelectedAssistant(assistant);
    setShowChatModal(true);
  };

  const handleCloseChatModal = () => {
    setShowChatModal(false);
    setSelectedAssistant(null);
  };

  const handleEditAssistant = (assistant) => {
    if (!assistant?.idOpenAIAssistant) return;
    navigate(`/assistants/${assistant.idOpenAIAssistant}/edit`);
  };

  const handleDeleteClick = (assistant) => {
    setAssistantToDelete(assistant);
  };

  const closeDeleteModal = () => {
    setAssistantToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!assistantToDelete?.idOpenAIAssistant) return;
    const assistantId = assistantToDelete.idOpenAIAssistant;
    setAssistantToDelete(null);
    try {
      setIsDeleting(true);
      setAlert({
        show: true,
        title: 'Eliminando asistente',
        message: 'Estamos eliminando el asistente seleccionado. Este proceso puede tardar unos segundos.',
        type: 'info'
      });

      await AsistenteService.eliminarAsistenteOpenAI(assistantId);

      setAlert({
        show: true,
        title: 'Asistente eliminado',
        message: 'El asistente se eliminó correctamente.',
        type: 'success'
      });
      setAsistentes(prev => prev.filter(asistente => asistente.idOpenAIAssistant !== assistantId));
    } catch (error) {
      console.error('Error al eliminar asistente:', error);
      setAlert({
        show: true,
        title: 'Error al eliminar asistente',
        message: error.message || 'No se pudo eliminar el asistente. Inténtalo nuevamente.',
        type: 'error'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <LoadingSpinner message="Verificando permisos..." />
        </div>
      </div>
    );
  }

  if (!isAllowedUser) {
    return (
      <div className="dashboard-container">
        <div
          style={{
            maxWidth: '640px',
            margin: '0 auto',
            padding: '2rem',
            backgroundColor: '#fff',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.1)',
            textAlign: 'center'
          }}
        >
          <h2>Acceso restringido</h2>
          <p>Esta sección está disponible únicamente para usuarios con rol Profesor o Admin.</p>
        </div>
      </div>
    );
  }

  // Formatear fecha para mostrar
  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    const parsed = fecha instanceof Date ? fecha : new Date(fecha);
    if (Number.isNaN(parsed.getTime())) return '-';

    return parsed.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Configuración de columnas para la tabla
  const columns = [
    {
      key: 'nombreAsistente',
      title: 'Nombre del Asistente',
      sortable: true,
      className: 'font-weight-bold'
    },
    {
      key: 'nombreArea',
      title: 'Área',
      sortable: true
    },
    {
      key: 'nombreMateria',
      title: 'Materia',
      sortable: true
    },
    {
      key: 'passwordAsistente',
      title: 'Password',
      sortable: false,
      render: (value, item) => item.passwordAsistente || item.PasswordAsistente || '—'
    },
    {
      key: 'fechaAlta',
      title: 'Fecha de Creación',
      sortable: true,
      align: 'center',
      width: 'lg',
      className: 'date-cell',
      formatter: formatFecha
    },
    {
      key: 'actions',
      title: 'Acciones',
      sortable: false,
      align: 'center',
      width: 'md',
      render: (value, item) => (
        <div className="table-actions">
          <button
            className="table-btn table-btn-edit"
            title="Editar asistente"
            onClick={() => handleEditAssistant(item)}
          >
            <i className="fas fa-pen"></i>
          </button>
          <button
            className="table-btn table-btn-delete"
            title="Eliminar asistente"
            onClick={() => handleDeleteClick(item)}
          >
            <i className="fas fa-trash"></i>
          </button>
          <button
            className="table-btn table-btn-test"
            title="Probar asistente"
            onClick={() => handleTestAssistant(item)}
          >
            <i className="fas fa-play"></i>
            <span>Probar</span>
          </button>
        </div>
      )
    }
  ];

  const handleNavigate = (path) => {
    setIsSidebarOpen(false);
    navigate(path);
  };

  return (
    <div className={`new-dashboard my-assistants ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>UTN FRC</span>
          </div>
        </div>
        
        <nav className="sidebar-nav">
          <button
            className="nav-item"
            onClick={() => handleNavigate('/dashboard')}
          >
            <i className="fas fa-tachometer-alt"></i>
            <span>Dashboard</span>
          </button>
          <button className="nav-item active">
            <i className="fas fa-list-ul"></i>
            <span>Mis Asistentes OpenAI</span>
          </button>
          {isGptClassicEnabled && (
            <button
              className="nav-item"
              onClick={() => handleNavigate('/gpt-assistants')}
            >
              <i className="fas fa-robot"></i>
              <span>Asistentes GPT</span>
            </button>
          )}
          <button
            className="nav-item"
            onClick={() => handleNavigate('/reports')}
          >
            <i className="fas fa-chart-bar"></i>
            <span>Reportes</span>
          </button>
        </nav>
        
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">Profesor</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              title="Mostrar menú"
            >
              <i className="fas fa-bars"></i>
            </button>
            <h1 className="page-title">
              <i className="fas fa-robot"></i>
              Mis Asistentes IA
            </h1>
            <p className="page-subtitle">Gestiona todos tus asistentes de inteligencia artificial</p>
          </div>
          
          <div className="header-right">
            <ActiveRegionalBadge />
            <ProcesosMenu />
            <button 
              className="btn btn-secondary"
              onClick={handleRefresh}
              disabled={isLoading}
              title="Actualizar lista"
            >
              <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i>
              Actualizar
            </button>
            <button className="logout-btn-header" onClick={handleLogout} title="Cerrar Sesión">
              <i className="fas fa-sign-out-alt"></i>
              <span className="logout-text">Salir</span>
            </button>
            
            <div className="user-menu">
              <button 
                className="user-menu-trigger"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="user-avatar">
                  <i className="fas fa-user"></i>
                </div>
                <i className="fas fa-chevron-down"></i>
              </button>
              
              {showUserMenu && (
                <div className="user-menu-dropdown">
                  <div className="dropdown-header">
                    <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
                    <span className="user-email">{user?.email || 'usuario@utn.edu.ar'}</span>
                  </div>
                  <div className="dropdown-divider"></div>
                  <button className="dropdown-item logout-item" onClick={handleLogout}>
                    <i className="fas fa-sign-out-alt"></i>
                    Cerrar Sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="dashboard-content">
          {/* Alert */}
          {alert.show && (
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={closeAlert}
            />
          )}

          {/* Assistants Summary */}
          <div className="assistants-summary">
            <div className="summary-card">
              <div className="summary-icon">
                <i className="fas fa-robot"></i>
              </div>
              <div className="summary-content">
                <h2 className="summary-number">{asistentes.length}</h2>
                <p className="summary-text">
                  {asistentes.length === 1 ? 'Asistente creado' : 'Asistentes creados'}
                </p>
                <div className="summary-description">
                  {asistentes.length === 0 
                    ? 'Aún no has creado ningún asistente IA'
                    : 'Listos para ayudar a los alumnos'
                  }
                </div>
              </div>
            </div>
          </div>

          {/* CTA Nuevo asistente */}
          <div className="assistant-cta">
            <div className="cta-text">
              <h3>Crea un nuevo asistente IA</h3>
              <p>Comparte material y habilítalo para tus alumnos en segundos.</p>
            </div>
            <button 
              className="btn btn-primary"
              onClick={handleNewAssistant}
            >
              <i className="fas fa-plus"></i>
              Nuevo Asistente
            </button>
          </div>

          {/* Table */}
          <div className="content-card">
            <div className="card-header">
              <h2 className="card-title">
                <i className="fas fa-list"></i>
                Lista de Asistentes
              </h2>
            </div>
            
            <div className="card-content">
              <DataTable
                data={asistentes}
                columns={columns}
                loading={isLoading}
                loadingMessage="Obteniendo mis asistentes..."
                emptyMessage="No tienes asistentes creados aún. ¡Crea tu primer asistente!"
                className="assistants-table"
              />
            </div>
          </div>
        </div>
      </main>

      {assistantToDelete && (
        <div className="confirm-modal-overlay">
          <div className="confirm-modal">
            <h3>¿Eliminar asistente?</h3>
            <p>A continuación, aceptando, usted eliminará el asistente junto con sus archivos, esta acción no se podrá deshacer. ¿Está usted seguro?</p>
            <div className="confirm-modal-actions">
              <button className="btn btn-outline" type="button" onClick={closeDeleteModal}>
                Cancelar
              </button>
              <button className="btn btn-danger" type="button" onClick={handleConfirmDelete}>
                Sí, eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat Modal */}
      <ChatModal
        isOpen={showChatModal}
        onClose={handleCloseChatModal}
        assistant={selectedAssistant}
      />
      {isDeleting && (
        <LoadingSpinner message="Eliminando asistente..." />
      )}
    </div>
  );
};

export default MyAssistants;
